<template>
  <CardSection :loading="isSavableStateLoading" :error="majorError">
    <template #header>
      {{ createNew ? 'New' : 'Edit' }} Template
    </template>
    <template #body>
      <SubSection :sub-section-heading="createNew ? '' : 'Edit'" :sub-section-nav="subSectionNav">
        <template #body>
          <VeeForm  v-if="selectedTemplate" @submit="onSubmit">
            <ChecklistTemplateDisabled :template="selectedTemplate"/>
            <div class="row">
              <MultiSelectInput v-model="selectedTemplate.templateCategoryIds"
                name="checklistTemplateCategories"
                label="Template Categories"
                :options="checklistStore.categories"
                label-key="category_name"
                value-key="id"
                rules="required"
                :multiple="true"
                :show-toggle-all="false"
                col-style="form-group-standard-column" />

              <MultiSelectInput v-model="selectedTemplate.organ_group_id"
                name="organ"
                label="Organ"
                :options="checklistStore.organGroups"
                label-key="name"
                value-key="id"
                rules="required"
                :disabled="selectedTemplate.hasInstance"
                col-style="form-group-standard-column" />

              <MultiSelectInput v-model="selectedTemplate.transplant_center_id"
                name="transplantCenter"
                label="Transplant Center"
                :options="checklistStore.transplantCenters"
                label-key="code"
                value-key="id"
                rules="required"
                :disabled="selectedTemplate.hasInstance"
                col-style="form-group-standard-column" />
            </div>

            <div class="row">
              <TextInput v-model="selectedTemplate.template_name"
                name="template_name"
                label="Template Name"
                rules="required"
                col-style="form-group-8-column-large" />
            </div>
            <div class="row">
              <TextArea v-model="selectedTemplate.notes"
                name="notes"
                label="Description/Notes - Internal"
                rows="3"
                cols="100" />
            </div>

            <div class="row">
              <TextInput v-model="selectedTemplate.reference_link"
                name="reference_link"
                label="Reference Link"
                col-style="form-group-standard-column" />
            </div>

            <div class="row">
              <div class="col-lg-4 col-md-4">
                <div class="form-check form-check-inline">
                  <input id="autoApply" 
                    v-model="selectedTemplate.auto_apply"
                    class="form-check-input" 
                    type="checkbox"
                    aria-labelledby="autoApply">
                  <label class="form-check-label" for="autoApply">
                    Auto apply template
                  </label>
                </div>
              </div>
            </div>

            <div class="row" v-if="createDuplicate">
              <div class="col-md-4 col-lg-4">
                <div class="form-check form-check-inline">
                  <input id="duplicate_items" 
                    v-model="selectedTemplate.duplicate_items"
                    class="form-check-input" 
                    type="checkbox"
                    aria-labelledby="duplicateItems">
                  <label class="form-check-label" for="duplicateItems">
                    Copy items from: {{ duplicatedTemplate.template_name }}
                  </label>
                </div>
              </div>
            </div>
         
            <!-- save controls -->
            <ActionToolbar :component-state="currentState" 
              :secondary-enabled="true"
              :success-message="successMessage"
              :tertiary-enabled="!createNew"
              primary-button-text="Save Template"
              :secondary-button-text="createNew ? 'Cancel' : 'Close'"
              :error-message="formError"
              :tertiary-button-text="selectedTemplate.disabled_at ? 'Enable' : 'Disable' "
              :toolbar-size="createNew ? 'col-12' : 'col-md-6'"
              @secondary-click="cancel()"
              @tertiary-click="toggle()">
            </ActionToolbar>
          </VeeForm>
        </template>
      </SubSection>
    </template>
  </CardSection>
</template>

<script lang="ts">
import CardSection from "@/src/components/shared/CardSection.vue";
import ActionToolbar from "@/src/components/shared/ActionToolbar.vue";
import ModalVariableReference from "@/src/components/admin-area/templates/ModalVariableReference.vue";
import ModalTemplatePreview from "@/src/components/admin-area/templates/ModalTemplatePreview.vue";
import SubSection from "@/src/components/shared/SubSection.vue";
import { Form as VeeForm } from 'vee-validate';
import { storeToRefs } from 'pinia'
import TextInput from "@/src/components/shared/FormComponents/TextInput.vue";
import TextArea from "@/src/components/shared/FormComponents/TextArea.vue";
import MultiSelectInput from "@/src/components/shared/FormComponents/MultiSelectInput.vue";
import { savableStateMixin } from "@/src/mixins/savableStateMixin";
import { useChecklistStore } from '@/src/stores/checklist';
import {checklistSubSectionNavMixin} from "@/src/components/admin-area/checklist/checklistSubSectionNavMixin";
import ChecklistTemplateDisabled from "@/src/components/admin-area/checklist/_checklistTemplateDisabled.vue";
import { Template } from "@/src/models/match/checklist/template";

export default {
  components: {
    CardSection,
    SubSection,
    ActionToolbar,
    VeeForm,
    ModalVariableReference,
    ModalTemplatePreview,
    TextInput,
    TextArea,
    MultiSelectInput,
    ChecklistTemplateDisabled
  },
  mixins: [ savableStateMixin, checklistSubSectionNavMixin ],
  setup() {
    const checklistStore = useChecklistStore();
    const { translateTxcCode, translateCategoryName } = storeToRefs(checklistStore)
    return {
      checklistStore, translateTxcCode, translateCategoryName
    }
  },
  data() {
    return {
      selectedTemplate: null,
      duplicatedTemplate: null,
      successMessage: null,
      newTemplate: null,
      copiedItems: [],
      isCopyMode: false
    }
  },
  computed: {
    createNew() {
      return !this.$route.params.id;
    },
    createDuplicate() {
      return !!this.$route.query.duplicate_from;
    },
    cancelText() {
      return this.createNew ? 'Cancel' : 'Close';
    },
    duplicatingFrom() {
      return this.$route.query.duplicate_from;
    }
  },
  async mounted() {
    try {
      this.setSavableStateLoading();
      await this.checklistStore.loadCategories();
      await this.checklistStore.loadTransplantCentres();
      await this.checklistStore.loadOrganGroups();
      const templateId = this.$route.params.id || this.$route.query.duplicate_from;
      const response = templateId ? await this.checklistStore.loadTemplate(templateId) : { data: {} }; 
      this.loadTemplateForm(response.data);
      this.setSavableStateIdle();
    } catch(error) {
      this.handleMajorError(error);
    }
  },
  methods: {
    submitPayload() {
      const payload = this.selectedTemplate.toAPIPayload();
      if(this.createNew && this.selectedTemplate.duplicate_items) {
        payload.duplicate_items_from = this.duplicatedTemplate.id;
      }
      return payload;
    },
    async onSubmit() {
      try {
        this.setSavableStateProcessing();
        const method = this.createNew ? this.checklistStore.createTemplate : this.checklistStore.updateTemplate; 
        const response: any = await method(this.submitPayload());
        
        if (this.createNew) this.$router.push({ name: 'admin_checklist_templates_edit', params: { id: response.data.id }});
        this.loadTemplateForm(response.data);
        
        this.successMessage = 'Successfully saved template';
        this.setSavableStateSuccess();
      } catch(error) {
        this.handleFormError(error);
      }
    },
    cancel() {
      this.$router.push({ name: 'admin_checklist_templates' });
    },
    async loadTemplateForm(templateAPIResponse: any) {
      this.selectedTemplate = Template.buildFromAPI(templateAPIResponse);
      if(this.createDuplicate) {
        this.selectedTemplate.duplicate_items = true;
        this.duplicatedTemplate = Template.buildFromAPI(templateAPIResponse);
      }
    },
    async toggle() {
      let response;
      try {
        this.setSavableStateProcessing();
        if(this.selectedTemplate.disabled_at) {
          if(!confirm(`Are you sure you want to enable the template (${this.selectedTemplate.template_name})?`)) {
            this.setSavableStateIdle();
            return;
          }
          response = await this.checklistStore.enableTemplate(this.selectedTemplate.id);
          this.successMessage = 'Successfully enabled template!';
        } else {
          if(!confirm(`Are you sure you want to disable the template (${this.selectedTemplate.template_name})?`)) {
            this.setSavableStateIdle();
            return;
          }
          response = await this.checklistStore.disableTemplate(this.selectedTemplate.id);
          this.successMessage = 'Successfully disabled template!';
        }
        this.loadTemplateForm(response.data);
        this.setSavableStateSuccess();
      } catch(error) {
        this.handleFormError(error);
      }
    }
  }
}
</script>
